/**
 * Nachrichtenliste eines Kanals: inkrementelles Nachladen (before/after/around, Vertrag 0.4), Scroll-Position
 * halten, Datumstrenner, Gruppierung, „Neu“-Linie, gelesen markieren (ack), Sprung zu Nachricht,
 * „Zum Ende springen“, optimistische eigene Nachrichten.
 */
import { compareSnowflakes, isDm, type ChannelDto } from '@lagerfeuer/shared';
import { ArrowDown, Hash, MessagesSquare } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ApiError, errorMessage } from '../../lib/api';
import { channelsApi, messagesApi } from '../../lib/endpoints';
import { markChannelOpen, onResync } from '../../realtime/bus';
import { upsertSorted } from '../../realtime/reducer';
import { useAppStore } from '../../store/app';
import { dmName } from '../../store/selectors';
import { toast } from '../../store/ui';
import { formatShortTime } from './format';
import { buildRows } from './grouping';
import { MessageItem } from './MessageItem';
import { PendingItem } from './PendingItem';
import type { MessagePerms } from './perms';
import { useMessagesUi } from './state';

export const PAGE_SIZE = 50;
/** Abstand zum Rand (px), ab dem nachgeladen wird. */
const LOAD_THRESHOLD = 400;
const EMPTY_PENDING: never[] = [];

interface Props {
  channel: ChannelDto;
  perms: MessagePerms;
  panel?: boolean;
  jumpToMessageId?: string | null;
  onJumped?: () => void;
}

export function MessageList({ channel, perms, panel, jumpToMessageId, onJumped }: Props) {
  const channelId = channel.id;
  const box = useAppStore((s) => s.messages[channelId]);
  const pending = useAppStore((s) => s.pending[channelId]) ?? EMPTY_PENDING;
  const me = useAppStore((s) => s.me);
  const compact = useAppStore((s) => s.settings?.messageDisplay === 'COMPACT');
  const lastRead = useAppStore((s) => s.readStates[channelId]?.lastReadMessageId ?? null);
  const addPage = useAppStore((s) => s.addPage);

  // Zuletzt gelesen beim Öffnen → Position der „Neu“-Linie.
  const [newAfterId, setNewAfterId] = useState<string | null>(() => {
    const s = useAppStore.getState();
    const rs = s.readStates[channelId]?.lastReadMessageId;
    const last = s.channels[channelId]?.lastMessageId;
    return rs && last && compareSnowflakes(last, rs) > 0 ? rs : null;
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<{ before: boolean; after: boolean }>({ before: false, after: false });
  const [atBottom, setAtBottom] = useState(true);
  const [farFromBottom, setFarFromBottom] = useState(false);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');

  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLOListElement>(null);
  const atBottomRef = useRef(true);
  const prependRef = useRef<number | null>(null);
  const scrollToRef = useRef<string | null>(null);
  const initialScrollRef = useRef(true);
  const loadingRef = useRef({ before: false, after: false });
  const ackedRef = useRef<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Generation des Ausschnitts: steigt, wenn er ersetzt wird; ältere Antworten werden verworfen. */
  const genRef = useRef(0);

  /** Ausschnitt geladen (Platzhalter während des Erstladens zählt nicht). */
  const loaded = !!box && !box.partial;
  const items = useMemo(() => box?.items ?? [], [box]);
  const showPending = !box?.hasMoreAfter;
  const rows = useMemo(
    () => buildRows(items, { newAfterId, meId: me?.id, pending: showPending ? pending : [] }, me),
    [items, newAfterId, me, pending, showPending],
  );

  // ---------- Laden ----------

  /** Neuester Ausschnitt; `toBottom` = nicht an der „Neu“-Linie, sondern am Ende positionieren. */
  /** Neue Generation: der Ausschnitt wird ersetzt, laufende before/after-Ladevorgänge sind hinfällig. */
  const nextGen = useCallback(() => {
    loadingRef.current = { before: false, after: false };
    setLoading({ before: false, after: false });
    return ++genRef.current;
  }, []);

  /** Ladefehler bei vorhandenem Ausschnitt: nicht als „am Ende“ behandeln (sonst hingen Live-Nachrichten hinter einer Lücke). */
  const markGap = useCallback(() => {
    useAppStore.getState().update((d) => {
      const b = d.messages[channelId];
      if (b && !b.partial) b.hasMoreAfter = true;
    });
  }, [channelId]);

  const loadInitial = useCallback(async (toBottom = false) => {
    const gen = nextGen();
    if (!perms.canReadHistory) {
      addPage(channelId, [], 'initial', PAGE_SIZE);
      return;
    }
    // Platzhalter sammelt Gateway-Nachrichten, die während der Anfrage eintreffen. Bei einem vorhandenen
    // Ausschnitt merken, was schon da war – nur danach Eingetroffenes überlebt das Ersetzen.
    const known = new Set((useAppStore.getState().messages[channelId]?.items ?? []).map((m) => m.id));
    useAppStore.getState().update((d) => {
      d.messages[channelId] ??= { items: [], hasMoreBefore: true, hasMoreAfter: false, partial: true };
    });
    try {
      const page = await messagesApi.list(channelId, { limit: PAGE_SIZE });
      if (genRef.current !== gen) return;
      atBottomRef.current = true;
      initialScrollRef.current = !toBottom;
      setError(null);
      const before = useAppStore.getState().messages[channelId];
      addPage(channelId, page, 'initial', PAGE_SIZE);
      const newest = page.reduce<string | null>((max, m) => (!max || compareSnowflakes(m.id, max) > 0 ? m.id : max), null);
      const live = before && !before.partial && !before.hasMoreAfter ? before.items.filter((m) => !known.has(m.id) && (!newest || compareSnowflakes(m.id, newest) > 0)) : [];
      if (live.length) {
        useAppStore.getState().update((d) => {
          const b = d.messages[channelId];
          if (b) for (const m of live) upsertSorted(b.items, m);
        });
      }
    } catch (e) {
      if (genRef.current !== gen) return;
      setError(errorMessage(e));
      markGap();
    }
  }, [channelId, perms.canReadHistory, addPage, nextGen, markGap]);

  const loadMore = useCallback(
    async (dir: 'before' | 'after') => {
      const cur = useAppStore.getState().messages[channelId];
      if (!cur || cur.partial || loadingRef.current[dir]) return;
      if (dir === 'before' ? !cur.hasMoreBefore : !cur.hasMoreAfter) return;
      const anchor = dir === 'before' ? cur.items[0] : cur.items[cur.items.length - 1];
      if (!anchor) return;
      const gen = genRef.current;
      loadingRef.current[dir] = true;
      setLoading({ ...loadingRef.current });
      try {
        const page = await messagesApi.list(channelId, { [dir]: anchor.id, limit: PAGE_SIZE });
        // Ausschnitt inzwischen ersetzt (Sprung, „Zum Ende springen“) → Seite passt nicht mehr.
        if (genRef.current !== gen) return;
        const el = scrollerRef.current;
        if (dir === 'before' && el) prependRef.current = el.scrollHeight - el.scrollTop;
        addPage(channelId, page, dir, PAGE_SIZE);
      } catch (e) {
        if (genRef.current === gen) toast.error('Nachrichten konnten nicht geladen werden', errorMessage(e));
      } finally {
        if (genRef.current === gen) {
          loadingRef.current[dir] = false;
          setLoading({ ...loadingRef.current });
        }
      }
    },
    [channelId, addPage],
  );

  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = useCallback((id: string) => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    // Asynchron setzen (läuft auch aus Effekten heraus).
    flashTimer.current = setTimeout(() => setHighlight(id), 0);
    highlightTimer.current = setTimeout(() => setHighlight(null), 2500);
  }, []);
  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  const scrollToMessage = useCallback((id: string): boolean => {
    if (!/^\d{1,20}$/.test(id)) return false;
    const el = scrollerRef.current?.querySelector<HTMLElement>(`[data-message-id="${id}"]`);
    if (!el) return false;
    el.scrollIntoView({ block: 'center' });
    el.focus({ preventScroll: true });
    return true;
  }, []);

  /** Zu einer Nachricht springen (ggf. Ausschnitt um sie herum laden). */
  const jumpTo = useCallback(
    async (id: string): Promise<boolean> => {
      const cur = useAppStore.getState().messages[channelId];
      if (!cur?.partial && cur?.items.some((m) => m.id === id)) {
        atBottomRef.current = false;
        initialScrollRef.current = false;
        if (!scrollToMessage(id)) scrollToRef.current = id;
        flash(id);
        return true;
      }
      const gen = nextGen();
      try {
        const page = await messagesApi.list(channelId, { around: id, limit: PAGE_SIZE });
        if (genRef.current !== gen) return false;
        if (!page.some((m) => m.id === id)) throw new ApiError(404, { statusCode: 404, error: 'NOT_FOUND', message: 'Nachricht nicht gefunden' });
        atBottomRef.current = false;
        initialScrollRef.current = false;
        scrollToRef.current = id;
        addPage(channelId, page, 'around', PAGE_SIZE);
        flash(id);
        return true;
      } catch (e) {
        if (genRef.current !== gen) return false;
        toast.error('Nachricht nicht gefunden', e instanceof ApiError && e.status === 404 ? 'Sie wurde gelöscht oder du kannst sie nicht sehen.' : errorMessage(e));
        const now = useAppStore.getState().messages[channelId];
        if (!now || now.partial) void loadInitial();
        return false;
      }
    },
    [channelId, addPage, flash, scrollToMessage, loadInitial, nextGen],
  );

  const jumpToPresent = useCallback(async () => {
    const cur = useAppStore.getState().messages[channelId];
    atBottomRef.current = true;
    await (cur?.hasMoreAfter ? loadInitial(true) : Promise.resolve());
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setAtBottom(true);
    setFarFromBottom(false);
  }, [channelId, loadInitial]);

  /** Oberste (teilweise) sichtbare Nachricht – Anker für das Neuladen nach einer Neuverbindung. */
  const firstVisibleId = useCallback((): string | null => {
    const el = scrollerRef.current;
    if (!el) return null;
    const top = el.getBoundingClientRect().top;
    for (const node of el.querySelectorAll<HTMLElement>('[data-message-id]')) {
      if (node.getBoundingClientRect().bottom > top) return node.dataset.messageId ?? null;
    }
    return null;
  }, []);

  /**
   * Nach einer Neuverbindung den sichtbaren Bereich neu laden – so kommen auch verpasste Bearbeitungen,
   * Löschungen und Reaktionen an. Am Ende: neueste Seite (ersetzt den Ausschnitt); sonst um die erste
   * sichtbare Nachricht herum.
   */
  const resync = useCallback(async () => {
    const cur = useAppStore.getState().messages[channelId];
    if (!cur || cur.partial || !perms.canReadHistory || (!cur.hasMoreAfter && atBottomRef.current)) {
      await loadInitial(true);
      return;
    }
    const anchor = firstVisibleId() ?? cur.items[0]?.id;
    if (!anchor) {
      await loadInitial(true);
      return;
    }
    const gen = nextGen();
    try {
      const page = await messagesApi.list(channelId, { around: anchor, limit: PAGE_SIZE });
      if (genRef.current !== gen) return;
      if (page.length === 0) {
        await loadInitial(true);
        return;
      }
      atBottomRef.current = false;
      initialScrollRef.current = false;
      if (page.some((m) => m.id === anchor)) scrollToRef.current = anchor;
      addPage(channelId, page, 'around', PAGE_SIZE);
    } catch {
      if (genRef.current === gen) markGap();
    }
  }, [channelId, perms.canReadHistory, addPage, loadInitial, firstVisibleId, nextGen, markGap]);

  useEffect(() => markChannelOpen(channelId), [channelId]);
  useEffect(() => onResync(() => void resync()), [resync]);

  // Erstes Laden bzw. Sprung aus der URL.
  const handledJump = useRef<string | null>(null);
  useEffect(() => {
    // Laden startet im Mikrotask (Zustandsänderungen nicht synchron im Effekt).
    const target = jumpToMessageId;
    if (target) {
      handledJump.current = target;
      void Promise.resolve()
        .then(() => jumpTo(target))
        .finally(() => onJumped?.());
    } else {
      void Promise.resolve().then(() => loadInitial());
    }
    // Nur beim Öffnen des Kanals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  // Spätere Sprünge (neuer ?message=-Parameter).
  useEffect(() => {
    const target = jumpToMessageId;
    if (!target || handledJump.current === target) return;
    handledJump.current = target;
    void Promise.resolve()
      .then(() => jumpTo(target))
      .finally(() => onJumped?.());
  }, [jumpToMessageId, jumpTo, onJumped]);

  // Sprünge innerhalb des Kanals (Antwort-Vorschau, Systemnachrichten).
  const jumpReq = useMessagesUi((s) => (s.jump?.channelId === channelId ? s.jump : null));
  useEffect(() => {
    if (!jumpReq) return;
    // Verarbeitet → löschen, sonst springt der Kanal beim nächsten Öffnen erneut.
    useMessagesUi.getState().clearJump(jumpReq.seq);
    void Promise.resolve().then(() => jumpTo(jumpReq.messageId));
  }, [jumpReq, jumpTo]);

  // Eigene Nachricht gesendet, während ein älterer Ausschnitt offen war → zum Ende.
  const pendingCount = pending.length;
  useEffect(() => {
    if (pendingCount > 0) {
      if (box?.hasMoreAfter) void Promise.resolve().then(jumpToPresent);
      else atBottomRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCount]);

  // ---------- Scrollen ----------

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (prependRef.current !== null) {
      el.scrollTop = el.scrollHeight - prependRef.current;
      prependRef.current = null;
      return;
    }
    if (scrollToRef.current) {
      if (scrollToMessage(scrollToRef.current)) scrollToRef.current = null;
      return;
    }
    if (initialScrollRef.current && loaded) {
      initialScrollRef.current = false;
      const divider = el.querySelector<HTMLElement>('[data-testid="new-messages-divider"]');
      if (divider && el.scrollHeight - divider.offsetTop > el.clientHeight) {
        el.scrollTop = Math.max(0, divider.offsetTop - 80);
        atBottomRef.current = false;
        return;
      }
    }
    if (atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [rows, box, loaded, scrollToMessage]);

  // Nachladende Bilder o. Ä. → am Ende bleiben (Liste existiert erst nach dem ersten Laden).
  const hasBox = loaded;
  useEffect(() => {
    const el = scrollerRef.current;
    const content = contentRef.current;
    if (!hasBox || !el || !content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [hasBox]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const bottom = dist < 40;
    atBottomRef.current = bottom && !useAppStore.getState().messages[channelId]?.hasMoreAfter;
    if (bottom !== atBottom) setAtBottom(bottom);
    const far = dist > el.clientHeight * 1.5;
    if (far !== farFromBottom) setFarFromBottom(far);
    if (el.scrollTop < LOAD_THRESHOLD) void loadMore('before');
    if (dist < LOAD_THRESHOLD) void loadMore('after');
  };

  // ---------- Gelesen markieren ----------

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, []);

  const lastId = useMemo(() => {
    for (let i = items.length - 1; i >= 0; i--) if (!items[i]!.deleted) return items[i]!.id;
    return null;
  }, [items]);

  const ack = useCallback(
    (id: string) => {
      ackedRef.current = id;
      channelsApi
        .ack(channelId, id)
        .then((rs) => rs && useAppStore.getState().dispatch('READ_STATE_UPDATE', rs))
        .catch(() => {
          ackedRef.current = null;
        });
    },
    [channelId],
  );

  useEffect(() => {
    if (!loaded || !atBottom || !visible || !lastId || box?.hasMoreAfter) return;
    if (lastRead && compareSnowflakes(lastId, lastRead) <= 0) return;
    if (ackedRef.current === lastId) return;
    const t = setTimeout(() => ack(lastId), 300);
    return () => clearTimeout(t);
  }, [loaded, atBottom, visible, lastId, lastRead, box?.hasMoreAfter, ack]);

  const markRead = () => {
    if (lastId) ack(lastId);
    setNewAfterId(null);
  };

  const onJump = useCallback((id: string) => void jumpTo(id), [jumpTo]);

  // ---------- Live-Region: nur neu eintreffende fremde Nachrichten ansagen ----------

  const [liveText, setLiveText] = useState('');
  const announcedRef = useRef<string | null>(null);
  const newest = loaded && !box?.hasMoreAfter ? items[items.length - 1] : undefined;
  useEffect(() => {
    if (!newest) return;
    const prev = announcedRef.current;
    announcedRef.current = newest.id;
    // Erstladen/Nachladen älterer Seiten nicht ansagen, nur echte Neuzugänge am Ende.
    if (!prev || compareSnowflakes(newest.id, prev) <= 0 || newest.deleted || newest.author?.id === me?.id) return;
    const who = newest.overrideName || newest.member?.nickname || newest.author?.displayName || newest.author?.username || 'Unbekannt';
    const text = newest.content ? `${who}: ${newest.content.slice(0, 200)}` : `Neue Nachricht von ${who}`;
    const t = setTimeout(() => setLiveText(text), 0);
    return () => clearTimeout(t);
  }, [newest, me?.id]);

  // ---------- Darstellung ----------

  if (error && !loaded) {
    return (
      <div className="msg-list-state empty" data-testid="message-list-error">
        <p>Nachrichten konnten nicht geladen werden: {error}</p>
        <button className="btn" onClick={() => void loadInitial()} data-testid="message-list-retry">
          Erneut versuchen
        </button>
      </div>
    );
  }

  const newDividerMsg = newAfterId ? items.find((m) => compareSnowflakes(m.id, newAfterId) > 0 && m.author?.id !== me?.id) : undefined;
  const showNewBar = !!newDividerMsg && !atBottom && !panel;

  return (
    <div className={`msg-list-wrap ${panel ? 'panel' : ''}`}>
      {showNewBar && (
        <div className="msg-new-bar" role="status" data-testid="new-messages-bar">
          <button type="button" className="msg-new-bar-text" onClick={() => onJump(newDividerMsg.id)}>
            Neue Nachrichten seit {formatShortTime(newDividerMsg.createdAt)}
          </button>
          <button type="button" className="msg-new-bar-action" onClick={markRead} data-testid="mark-read">
            Als gelesen markieren
          </button>
        </div>
      )}
      <div className="msg-scroller scroll" ref={scrollerRef} onScroll={onScroll} data-testid="message-list" aria-busy={!loaded || loading.before || loading.after}>
        {!box || !loaded ? (
          <div className="msg-list-state empty" data-testid="message-list-loading">
            <div className="spinner" aria-label="Nachrichten werden geladen" />
          </div>
        ) : (
          <>
            {box.hasMoreBefore ? (
              <div className="msg-loader" data-testid="message-list-loading-before">
                {loading.before ? <div className="spinner" /> : <span className="faint small">Ältere Nachrichten werden geladen …</span>}
              </div>
            ) : (
              <ChannelStart channel={channel} meId={me?.id} canRead={perms.canReadHistory} panel={panel} />
            )}
            <ol className="msg-list" ref={contentRef} role="list" aria-label="Nachrichten">
              {rows.map((r) => {
                switch (r.kind) {
                  case 'day':
                    return (
                      <li key={r.key} className="msg-divider" role="separator" data-testid="date-divider">
                        <span>{r.label}</span>
                      </li>
                    );
                  case 'new':
                    return (
                      <li key={r.key} className="msg-divider msg-divider-new" role="separator" aria-label="Neue Nachrichten" data-testid="new-messages-divider">
                        <span>Neu</span>
                      </li>
                    );
                  case 'message':
                    return (
                      <MessageItem
                        key={r.key}
                        message={r.message}
                        grouped={r.grouped}
                        channel={channel}
                        perms={perms}
                        compact={compact}
                        panel={panel}
                        highlighted={highlight === r.message.id}
                        onJump={onJump}
                      />
                    );
                  case 'pending':
                    return <PendingItem key={r.key} pending={r.pending} grouped={r.grouped} serverId={channel.serverId} panel={panel} />;
                }
              })}
            </ol>
            {box.hasMoreAfter && (
              <div className="msg-loader" data-testid="message-list-loading-after">
                {loading.after ? <div className="spinner" /> : <span className="faint small">Neuere Nachrichten werden geladen …</span>}
              </div>
            )}
            <div className="msg-list-end" aria-hidden="true" />
            <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="message-live-region">
              {liveText}
            </div>
          </>
        )}
      </div>
      {loaded && (box.hasMoreAfter || farFromBottom) && (
        <button type="button" className="msg-jump-present" onClick={() => void jumpToPresent()} data-testid="jump-to-present">
          {box.hasMoreAfter ? 'Du siehst ältere Nachrichten' : 'Neuere Nachrichten'} <span className="msg-jump-present-action">Zum Ende springen <ArrowDown size={14} /></span>
        </button>
      )}
    </div>
  );
}

function ChannelStart({ channel, meId, canRead, panel }: { channel: ChannelDto; meId?: string; canRead: boolean; panel?: boolean }) {
  const dm = isDm(channel.type);
  const name = dm ? dmName(channel, meId) : channel.name;
  return (
    <div className={`channel-start ${panel ? 'panel' : ''}`} data-testid="channel-start">
      <div className="channel-start-icon" aria-hidden="true">
        {dm ? <MessagesSquare size={32} /> : <Hash size={32} />}
      </div>
      <h2>{dm ? name : `Willkommen in #${name}!`}</h2>
      <p className="muted">
        {!canRead
          ? 'Du kannst den bisherigen Verlauf dieses Kanals nicht sehen.'
          : dm
            ? channel.type === 'DM'
              ? `Das ist der Anfang deiner Direktnachrichten mit ${name}.`
              : 'Das ist der Anfang dieser Gruppe.'
            : `Das ist der Anfang des Kanals #${name}.`}
      </p>
    </div>
  );
}

